import React, { useMemo, useState } from "react";
import TitleButtons from "../../../components/TitleButtons";
import TextField from "../../../components/TextField";
import Button from "../../../components/Button";

const API_URL =
  "https://api.eva-googlemarine.com/departamentos/coordenacao/empresa-clientes";

const onlyDigits = (s: unknown) => String(s ?? "").replace(/\D+/g, "");
function maskCNPJ(s: string): string {
  const d = onlyDigits(s).slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return d.replace(/^(\d{2})(\d+)/, "$1.$2");
  if (d.length <= 8) return d.replace(/^(\d{2})(\d{3})(\d+)/, "$1.$2.$3");
  if (d.length <= 12)
    return d.replace(/^(\d{2})(\d{3})(\d{3})(\d+)/, "$1.$2.$3/$4");
  return d.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2}).*/,
    "$1.$2.$3/$4-$5"
  );
}
function isValidEmail(x: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(x);
}

const NoticeBanner: React.FC<{
  notice: { type: "success" | "error" | "notify"; text: string };
  onClose?: () => void;
  sticky?: boolean;
}> = ({ notice, onClose, sticky = true }) => {
  let bg, color, border;
  switch (notice.type) {
    case "success":
      bg = "#e6ffed";
      color = "#0f5132";
      border = "#badbcc";
      break;
    case "error":
      bg = "#ffecec";
      color = "#842029";
      border = "#f5c2c7";
      break;
    default:
      bg = "#e7f1ff";
      color = "#084298";
      border = "#b6d4fe";
      break;
  }
  return (
    <div
      role="alert"
      style={{
        margin: "12px 0",
        padding: "10px 12px",
        borderRadius: 8,
        background: bg,
        color,
        border: `1px solid ${border}`,
        position: sticky ? "sticky" : "static",
        top: 0,
        zIndex: 20,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ flex: 1 }}>{notice.text}</span>
        {onClose && (
          <button
            type="button"
            aria-label="Fechar aviso"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              fontSize: 16,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};

const RegistrarEmpresa: React.FC = () => {
  // obrigatórios
  const [razao, setRazao] = useState("");
  const [fantasia, setFantasia] = useState("");
  const [cnpjView, setCnpjView] = useState("");

  // opcionais com opção "pendente"
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [emailPendente, setEmailPendente] = useState(false);
  const [telefonePendente, setTelefonePendente] = useState(false);

  // opcionais de endereço/IE
  const [ie, setIE] = useState("");
  const [logradouro, setLogradouro] = useState("");
  const [numero, setNumero] = useState("");
  const [bairro, setBairro] = useState("");
  const [cidade, setCidade] = useState("");
  const [uf, setUF] = useState("");
  const [cep, setCEP] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<null | {
    type: "success" | "error" | "notify";
    text: string;
  }>(null);

  const onChangeCNPJ = (v: string) => setCnpjView(maskCNPJ(v));
  const onlyDigitsStr = (s: string) => onlyDigits(s);

  function clearAll() {
    setRazao("");
    setFantasia("");
    setCnpjView("");
    setEmail("");
    setTelefone("");
    setEmailPendente(false);
    setTelefonePendente(false);
    setIE("");
    setLogradouro("");
    setNumero("");
    setBairro("");
    setCidade("");
    setUF("");
    setCEP("");
  }

  // Habilita/Desabilita botão conforme obrigatórios
  const cnpjDigits = onlyDigitsStr(cnpjView);
  const canSubmit = useMemo(() => {
    const obrigatoriosOk =
      razao.trim().length > 0 &&
      fantasia.trim().length > 0 &&
      cnpjDigits.length === 14;

    const emailOk =
      emailPendente || (email.trim().length > 0 && isValidEmail(email.trim()));

    const telefoneOk = telefonePendente || telefone.trim().length > 0;

    // Apenas bloqueia pelo conjunto de obrigatórios + consistência dos opcionais quando informados
    return (
      obrigatoriosOk &&
      (!email || isValidEmail(email) || emailPendente) &&
      (telefonePendente || telefone.trim().length > 0 || !telefone)
    );
  }, [
    razao,
    fantasia,
    cnpjDigits,
    email,
    telefone,
    emailPendente,
    telefonePendente,
  ]);

  async function handleRegister() {
    setNotice(null);

    const erros: string[] = [];

    // obrigatórios
    if (!razao.trim()) erros.push("Informe a Razão Social.");
    if (!fantasia.trim()) erros.push("Informe o Nome Fantasia.");
    if (!cnpjDigits || cnpjDigits.length !== 14) erros.push("CNPJ inválido.");

    // opcionais com regra "pendente"
    if (!emailPendente) {
      if (!email.trim())
        erros.push("Informe o e-mail ou marque como 'Não informar agora'.");
      else if (!isValidEmail(email.trim())) erros.push("E-mail inválido.");
    }
    if (!telefonePendente) {
      if (!telefone.trim())
        erros.push("Informe o telefone ou marque como 'Não informar agora'.");
    }

    if (erros.length) {
      setNotice({ type: "error", text: erros.join(" ") });
      return;
    }

    const payload: any = {
      razao_social: razao.trim(),
      nome_fantasia: fantasia.trim(),
      cnpj: cnpjDigits,
      // política:
      // - se pendente, envia como null + status "pendente" (remova os *_status se sua API não aceitar)
      email: emailPendente ? null : email.trim(),
      telefone: telefonePendente ? null : telefone.trim(),
      ...(emailPendente ? { email_status: "pendente informar" } : {}),
      ...(telefonePendente ? { telefone_status: "pendente informar" } : {}),
    };

    if (ie.trim()) payload.ie = ie.trim();
    if (logradouro.trim()) payload.endereco_logradouro = logradouro.trim();
    if (numero.trim()) payload.endereco_numero = numero.trim();
    if (bairro.trim()) payload.endereco_bairro = bairro.trim();
    if (cidade.trim()) payload.endereco_cidade = cidade.trim();
    if (uf.trim()) payload.endereco_uf = uf.trim().slice(0, 2).toUpperCase();
    if (cep.trim()) payload.endereco_cep = cep.trim();

    setSubmitting(true);
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) {
        const msg =
          data?.erro ||
          (Array.isArray(data?.erros) && data.erros.join(" | ")) ||
          data?.message ||
          "Falha ao registrar empresa.";
        setNotice({ type: "error", text: msg });
        return;
      }
      setNotice({
        type: "success",
        text: `Empresa registrada! ID ${data?.data?.id ?? ""}.`,
      });
      clearAll();
    } catch (err: any) {
      setNotice({
        type: "error",
        text: `Erro de rede: ${err?.message || "tente novamente."}`,
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ width: "100%" }}>
      <TitleButtons title="Coordenação - Registrar Empresa" />

      {(notice?.type === "success" || notice?.type === "notify") && (
        <NoticeBanner notice={notice} onClose={() => setNotice(null)} />
      )}

      {/* Container 100% largura */}
      <div style={{ display: "grid", gap: 10, width: "100%" }}>
        {/* ===== Obrigatórios (100%) ===== */}
        <div style={{ width: "100%" }}>
          <TextField
            label="Razão Social *"
            value={razao}
            onChange={setRazao}
            placeholder="Ex.: Marinetec Equipamentos e Serviços LTDA"
            uppercase
            style={{ width: "100%" }}
          />
        </div>

        <div style={{ width: "100%" }}>
          <TextField
            label="Nome Fantasia *"
            value={fantasia}
            onChange={setFantasia}
            placeholder="Ex.: Marinetec"
            uppercase
            style={{ width: "100%" }}
          />
        </div>

        <div style={{ width: "100%" }}>
          <TextField
            label="CNPJ *"
            value={cnpjView}
            onChange={onChangeCNPJ}
            placeholder="00.000.000/0000-00"
            style={{ width: "100%" }}
          />
        </div>

        {/* ===== E-mail (opcional com pendência) ===== */}
        <div style={{ width: "100%" }}>
          <TextField
            label="E-mail"
            value={email}
            onChange={setEmail}
            placeholder="contato@empresa.com.br"
            style={{ width: "100%" }}
            disabled={emailPendente}
          />
          <div
            style={{
              marginTop: 6,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <input
              id="email-pendente"
              type="checkbox"
              checked={emailPendente}
              onChange={(e) => setEmailPendente(e.target.checked)}
            />
            <label htmlFor="email-pendente" style={{ fontSize: 12 }}>
              Não informar agora (marcar como pendente)
            </label>
          </div>
        </div>

        {/* ===== Telefone (opcional com pendência) ===== */}
        <div style={{ width: "100%" }}>
          <TextField
            label="Telefone"
            value={telefone}
            onChange={setTelefone}
            placeholder="(11) 1234-5678"
            style={{ width: "100%" }}
            disabled={telefonePendente}
          />
          <div
            style={{
              marginTop: 6,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <input
              id="tel-pendente"
              type="checkbox"
              checked={telefonePendente}
              onChange={(e) => setTelefonePendente(e.target.checked)}
            />
            <label htmlFor="tel-pendente" style={{ fontSize: 12 }}>
              Não informar agora (marcar como pendente)
            </label>
          </div>
        </div>

        {/* ===== Opcionais (100%) ===== */}
        <div style={{ width: "100%" }}>
          <TextField
            label="Inscrição Estadual (IE) (opcional)"
            value={ie}
            onChange={setIE}
            placeholder="Opcional"
            style={{ width: "100%" }}
          />
        </div>

        <div style={{ width: "100%" }}>
          <TextField
            label="Logradouro (opcional)"
            value={logradouro}
            onChange={setLogradouro}
            placeholder="Rua, Av., etc."
            style={{ width: "100%" }}
          />
        </div>

        <div style={{ width: "100%" }}>
          <TextField
            label="Número (opcional)"
            value={numero}
            onChange={setNumero}
            placeholder="Nº"
            style={{ width: "100%" }}
          />
        </div>

        <div style={{ width: "100%" }}>
          <TextField
            label="Bairro (opcional)"
            value={bairro}
            onChange={setBairro}
            placeholder="Bairro"
            style={{ width: "100%" }}
          />
        </div>

        <div style={{ width: "100%" }}>
          <TextField
            label="Cidade (opcional)"
            value={cidade}
            onChange={setCidade}
            placeholder="Cidade"
            style={{ width: "100%" }}
          />
        </div>

        <div style={{ width: "100%" }}>
          <TextField
            label="Unidade Federativa (UF) (opcional)"
            value={uf}
            onChange={setUF}
            placeholder="UF"
            uppercase
            style={{ width: "100%" }}
          />
        </div>

        <div style={{ width: "100%" }}>
          <TextField
            label="CEP (opcional)"
            value={cep}
            onChange={setCEP}
            placeholder="00000-000"
            style={{ width: "100%" }}
          />
        </div>
      </div>

      {notice?.type === "error" && (
        <NoticeBanner
          notice={notice}
          onClose={() => setNotice(null)}
          sticky={false}
        />
      )}

      <div style={{ marginTop: 12 }}>
        <Button
          text={submitting ? "Registrando..." : "Registrar Empresa"}
          onClick={handleRegister}
          disabled={submitting || !canSubmit}
        />
      </div>
    </div>
  );
};

export default RegistrarEmpresa;
